import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { landingAnimationAPI } from '../../services/api';

const sizeMap = { sm: 'text-2xl', md: 'text-4xl', lg: 'text-5xl', xl: 'text-6xl' };
const speedMap = { slow: 6, normal: 4, fast: 2 };

const animationVariants = {
  float: (speed) => ({
    animate: { y: [0, -20, 0], rotate: [0, 5, -5, 0] },
    transition: { duration: speed, repeat: Infinity, ease: 'easeInOut' },
  }),
  pulse: (speed) => ({
    animate: { scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] },
    transition: { duration: speed, repeat: Infinity },
  }),
  orbit: (speed) => ({
    animate: { rotate: 360 },
    transition: { duration: speed * 3, repeat: Infinity, ease: 'linear' },
  }),
  shimmer: (speed) => ({
    animate: { opacity: [0.3, 1, 0.3], x: [0, 10, 0] },
    transition: { duration: speed, repeat: Infinity },
  }),
  wave: (speed) => ({
    animate: { y: [0, -15, 0, 15, 0], x: [0, 8, 0, -8, 0] },
    transition: { duration: speed * 1.5, repeat: Infinity },
  }),
  bounce: (speed) => ({
    animate: { y: [0, -30, 0] },
    transition: { duration: speed * 0.8, repeat: Infinity, type: 'spring', stiffness: 200 },
  }),
  glow: (speed) => ({
    animate: { scale: [1, 1.15, 1], filter: ['brightness(1)', 'brightness(1.5)', 'brightness(1)'] },
    transition: { duration: speed, repeat: Infinity },
  }),
  particle: (speed) => ({
    animate: { y: [0, -40, 0], opacity: [0, 1, 0], scale: [0.5, 1, 0.5] },
    transition: { duration: speed * 1.2, repeat: Infinity },
  }),
};

export default function LandingAnimations() {
  const [animations, setAnimations] = useState([]);

  useEffect(() => {
    landingAnimationAPI
      .getAll({ enabled: 'true' })
      .then((res) => setAnimations(res.data.data))
      .catch(() => setAnimations([]));
  }, []);

  if (!animations.length) return null;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
      {animations.map((anim) => {
        const speed = speedMap[anim.speed] || 4;
        const variant = animationVariants[anim.animationType]?.(speed) || animationVariants.float(speed);
        return (
          <motion.div
            key={anim.id}
            className={`absolute ${sizeMap[anim.size] || 'text-4xl'} select-none`}
            style={{
              left: anim.positionX,
              top: anim.positionY,
              color: anim.color,
              textShadow: `0 0 20px ${anim.color}40`,
            }}
            animate={variant.animate}
            transition={variant.transition}
            title={anim.name}
          >
            {anim.emoji}
          </motion.div>
        );
      })}
    </div>
  );
}
